/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
// Dialog surfaces for the redesigned screens: a centered Modal (also used for
// confirmations, role="alertdialog") and a right-hand SideSheet.
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

export function CloseButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
    >
      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "max-w-[480px]",
  role = "dialog",
}) {
  return (
    <Dialog open={open} onClose={onClose} role={role} className="relative z-50">
      <div className="fixed inset-0 bg-gray-900/45" aria-hidden="true" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-start justify-center px-4 py-16 sm:py-24">
          <DialogPanel
            className={`flex w-full ${size} flex-col gap-4 rounded-2xl bg-white p-6 shadow-xl`}
          >
            <div className="flex flex-col gap-1">
              <DialogTitle
                as="h2"
                className="text-[19px] font-semibold text-gray-900"
              >
                {title}
              </DialogTitle>
              {description && (
                <p className="text-sm text-gray-600">{description}</p>
              )}
            </div>
            {children}
            {footer && (
              <div className="flex flex-wrap justify-end gap-2">{footer}</div>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

export function SideSheet({
  open,
  onClose,
  title,
  description,
  closeLabel,
  children,
  footer,
  onSubmit,
}) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-gray-900/45" aria-hidden="true" />
      <div className="fixed inset-0 flex justify-end">
        <DialogPanel className="flex h-full w-full max-w-[460px] flex-col bg-white shadow-xl">
          <form
            onSubmit={onSubmit}
            noValidate
            className="flex h-full flex-col gap-5 overflow-y-auto px-6 py-7"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-1">
                <DialogTitle
                  as="h2"
                  className="text-xl font-semibold text-gray-900"
                >
                  {title}
                </DialogTitle>
                {description && (
                  <p className="text-[13px] leading-normal text-gray-600">
                    {description}
                  </p>
                )}
              </div>
              <CloseButton onClick={() => onClose(false)} label={closeLabel} />
            </div>
            {children}
            {footer && (
              <div className="mt-auto flex flex-wrap justify-end gap-2 pt-2">
                {footer}
              </div>
            )}
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

// Inline API error: one message as a paragraph, several as a list.
export function ErrorMessages({ messages, className = "" }) {
  if (!messages || messages.length === 0) return null;
  return (
    <div
      role="alert"
      className={`rounded-[10px] bg-red-50 px-3 py-2.5 text-sm text-red-800 ${className}`}
    >
      {messages.length === 1 ? (
        <p className="m-0">{messages[0]}</p>
      ) : (
        <ul className="m-0 list-disc pl-5">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
